import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../utils/supabaseClient';
import LanguageDropdown from '../components/LanguageDropdown';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const Header = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 18px;
  color: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const HeaderGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.9);
  padding: 1.5rem;
  border-radius: 18px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #4ecdc4;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: #666;
`;

const RoomsSection = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 18px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #333;
`;

const RoomGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const RoomCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: 2px solid ${props => props.isActive ? '#4ecdc4' : '#ddd'};
`;

const RoomHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const RoomCode = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #4ecdc4;
`;

const RoomStatus = styled.div`
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => props.isActive ? '#4ecdc4' : '#6c757d'};
  color: white;
`;

const RoomInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
`;

const InfoLabel = styled.span`
  color: #666;
`;

const InfoValue = styled.span`
  font-weight: 600;
  color: #333;
`;

const RoomActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionButton = styled(motion.button)`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return 'background: #4ecdc4; color: white;';
      case 'danger':
        return 'background: #dc3545; color: white;';
      case 'warning':
        return 'background: #ffc107; color: #212529;';
      default:
        return 'background: #6c757d; color: white;';
    }
  }}
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(78, 205, 196, 0.3);
  border-radius: 50%;
  border-top: 4px solid #4ecdc4;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 2rem auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const BackButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
`;

const RoomManagement = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, isPremium } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRooms: 0,
    activeRooms: 0,
    totalPlayers: 0
  });

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchRooms();
  }, [user, navigate]);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      
      const { data: roomsData, error: roomsError } = await supabase
        .from('game_rooms')
        .select(`
          *,
          players(id, player_name, is_connected)
        `)
        .eq('host_user_id', user.id)
        .order('created_at', { ascending: false });

      if (roomsError) throw roomsError;

      setRooms(roomsData || []);

      const totalRooms = roomsData?.length || 0;
      const activeRooms = roomsData?.filter(room => room.is_active).length || 0;
      const totalPlayers = roomsData?.reduce((sum, room) => sum + (room.players?.length || 0), 0) || 0;

      setStats({ totalRooms, activeRooms, totalPlayers });

    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinRoom = (roomCode) => {
    navigate(`/room/${roomCode}`);
  };

  const handleDeactivateRoom = async (roomId) => {
    if (!window.confirm(t('confirmDeactivateRoom'))) return;

    try {
      const { error } = await supabase
        .from('game_rooms')
        .update({ is_active: false })
        .eq('id', roomId)
        .eq('host_user_id', user.id);

      if (error) throw error;
      await fetchRooms();
      alert('Room deactivated successfully!');
    } catch (error) {
      console.error('Error deactivating room:', error);
      alert(`${t('errorDeactivatingRoom')}: ${error.message}`);
    }
  };

  const handleReactivateRoom = async (roomId) => {
    try {
      const { error } = await supabase
        .from('game_rooms')
        .update({ is_active: true })
        .eq('id', roomId)
        .eq('host_user_id', user.id);

      if (error) throw error;
      await fetchRooms();
      alert('Room reactivated successfully!');
    } catch (error) {
      console.error('Error reactivating room:', error);
      alert(`${t('errorReactivatingRoom')}: ${error.message}`);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm(t('confirmDeleteRoom'))) return;

    try {
      console.log('Attempting to delete room with ID:', roomId);
      
      // Check if user is authenticated and is the room owner
      if (!user) {
        throw new Error('You must be logged in to delete rooms');
      }

      // First, delete all players in the room
      const { error: playersError } = await supabase
        .from('players')
        .delete()
        .eq('game_room_id', roomId);

      if (playersError) {
        console.error('Error deleting players:', playersError);
        throw new Error(`Failed to delete players: ${playersError.message}`);
      }

      console.log('Players deleted successfully');

      // Then delete the room - make sure to filter by both ID and host_user_id for RLS
      const { error: roomError } = await supabase
        .from('game_rooms')
        .delete()
        .eq('id', roomId)
        .eq('host_user_id', user.id);

      if (roomError) {
        console.error('Error deleting room:', roomError);
        throw new Error(`Failed to delete room: ${roomError.message}`);
      }

      console.log('Room deleted successfully');
      await fetchRooms();
      
      // Show success message
      if (window.confirm) {
        alert('Room deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting room:', error);
      alert(`${t('errorDeletingRoom')}: ${error.message}`);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Title onClick={() => navigate('/')}>Parteezy</Title>
          <HeaderGroup>
            <LanguageDropdown />
            <BackButton onClick={() => navigate('/')}>
              {t('backToHome')}
            </BackButton>
          </HeaderGroup>
        </Header>
        <Content>
          <LoadingSpinner />
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title onClick={() => navigate('/')}>Parteezy</Title>
        <HeaderGroup>
          <LanguageDropdown />
          <BackButton onClick={() => navigate('/')}>
            {t('backToHome')}
          </BackButton>
        </HeaderGroup>
      </Header>

      <Content>
        <StatsSection>
          <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <StatNumber>{stats.totalRooms}</StatNumber>
            <StatLabel>{t('totalRooms')}</StatLabel>
          </StatCard>

          <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <StatNumber>{stats.activeRooms}</StatNumber>
            <StatLabel>{t('activeRooms')}</StatLabel>
          </StatCard>

          <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <StatNumber>{stats.totalPlayers}</StatNumber>
            <StatLabel>{t('totalPlayers')}</StatLabel>
          </StatCard>

          {isPremium && (
            <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <StatNumber>{6 - stats.activeRooms}</StatNumber>
              <StatLabel>{t('roomsRemaining')}</StatLabel>
            </StatCard>
          )}
        </StatsSection>

        <RoomsSection>
          <SectionTitle>{t('myRooms')}</SectionTitle>
          
          {rooms.length === 0 ? (
            <EmptyState>
              <h3>{t('noRoomsYet')}</h3>
              <p>{t('createFirstRoom')}</p>
            </EmptyState>
          ) : (
            <RoomGrid>
              {rooms.map((room, index) => (
                <RoomCard
                  key={room.id}
                  isActive={room.is_active}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <RoomHeader>
                    <RoomCode>{room.room_code}</RoomCode>
                    <RoomStatus isActive={room.is_active}>
                      {room.is_active ? t('active') : t('inactive')}
                    </RoomStatus>
                  </RoomHeader>

                  <RoomInfo>
                    <InfoItem>
                      <InfoLabel>{t('created')}:</InfoLabel>
                      <InfoValue>{formatDate(room.created_at)}</InfoValue>
                    </InfoItem>
                    
                    <InfoItem>
                      <InfoLabel>{t('players')}:</InfoLabel>
                      <InfoValue>{room.players?.length || 0}</InfoValue>
                    </InfoItem>
                    
                    <InfoItem>
                      <InfoLabel>{t('type')}:</InfoLabel>
                      <InfoValue>
                        {room.is_private ? `${t('private')} 🔒` : t('public')}
                      </InfoValue>
                    </InfoItem>
                    
                    <InfoItem>
                      <InfoLabel>{t('lastActivity')}:</InfoLabel>
                      <InfoValue>{formatDate(room.last_activity)}</InfoValue>
                    </InfoItem>
                  </RoomInfo>

                  <RoomActions>
                    {room.is_active && (
                      <ActionButton
                        variant="primary"
                        onClick={() => handleJoinRoom(room.room_code)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {t('joinRoom')}
                      </ActionButton>
                    )}
                    
                    {room.is_active ? (
                      <ActionButton
                        variant="warning"
                        onClick={() => handleDeactivateRoom(room.id)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {t('deactivate')}
                      </ActionButton>
                    ) : (
                      <ActionButton
                        variant="primary"
                        onClick={() => handleReactivateRoom(room.id)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {t('reactivate')}
                      </ActionButton>
                    )}
                    
                    <ActionButton
                      variant="danger"
                      onClick={() => handleDeleteRoom(room.id)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {t('delete')}
                    </ActionButton>
                  </RoomActions>
                </RoomCard>
              ))}
            </RoomGrid>
          )}
        </RoomsSection>
      </Content>
    </Container>
  );
};

export default RoomManagement; 